// Global Instructions Rule Applied!
// Shape returned by view__fund_closing_earlier_years, the Closing form's
// informational "Earlier years" read (docs/LATE_CLOSER_PNL_ALLOCATION_DESIGN.md
// section 5, PR 3): what the partners a closing admits will take extra of
// (and the others less) per K-1 tax category over the coming years - the
// engine's carry-forward at the closing date for the open year, with what
// the year's items to that date already cure. A Draft closing is previewed
// with its selection applied (nothing written); a fund on Static has
// nothing to show (per_fin_year false). Mirrors backend/src/data_manager/
// data_models/fund_closing_earlier_years_record.py. Ids are plain strings.
import { z } from 'zod'

export const ClosingEarlierYearsRowSchema = z
  .object({
    investors__id: z.string(),
    investors__name: z.string(),
    prog__is_new: z.boolean(),
    tax_categories__id: z.number().int().nullish(),
    tax_categories__label: z.string(),
    prog__share: z.number(),
    prog__want: z.number(),
    prog__want_fmt: z.string().nullish(),
    prog__curative: z.number(),
    prog__curative_fmt: z.string().nullish(),
    prog__carry_forward: z.number(),
    prog__carry_forward_fmt: z.string().nullish()
  })
  .strict()

export type ClosingEarlierYearsRow = z.infer<typeof ClosingEarlierYearsRowSchema>

export const ClosingEarlierYearsCategorySchema = z
  .object({
    tax_categories__id: z.number().int().nullish(),
    tax_categories__label: z.string(),
    prog__new_carry_forward: z.number().nullish(),
    prog__new_carry_forward_fmt: z.string().nullish(),
    prog__others_carry_forward: z.number().nullish(),
    prog__others_carry_forward_fmt: z.string().nullish(),
    prog__curative: z.number().nullish(),
    prog__net: z.number().nullish()
  })
  .strict()

export type ClosingEarlierYearsCategory = z.infer<typeof ClosingEarlierYearsCategorySchema>

export const FundClosingEarlierYearsSchema = z
  .object({
    per_fin_year: z.boolean(),
    basis_label: z.string(),
    is_booked: z.boolean(),
    closing_date: z.string().nullish(),
    fin_year: z
      .object({
        id: z.string(),
        fin_year: z.number().int(),
        year_start_date: z.string().nullish(),
        year_end_date: z.string().nullish()
      })
      .strict()
      .nullish(),
    rows: z.array(ClosingEarlierYearsRowSchema),
    categories: z.array(ClosingEarlierYearsCategorySchema),
    status_message: z.string().nullish()
  })
  .strict()

export type FundClosingEarlierYears = z.infer<typeof FundClosingEarlierYearsSchema>

export const validateFundClosingEarlierYears = (data: unknown): FundClosingEarlierYears => {
  const result = FundClosingEarlierYearsSchema.safeParse(data)
  if (!result.success) {
    throw new Error(`Closing earlier years validation failed: ${result.error.message}`)
  }
  return result.data
}
